import React from "react";
import { Box, useTheme } from "@mui/material";
import { BasketPlotLayer } from "./BasketPlotLayer";
import { BasketMaterial } from "./types";

const TOTAL_HEIGHT = 450;

// Extract height calculation logic
const calculateSegmentHeights = (
  materials: BasketMaterial[],
  totalMass: number,
  height: number,
  fullness: number
) => {
  const availableHeight = height; // Always use 100% of basket height
  const minPercentage = Math.min(0.1, 1 / materials.length);

  const materialData = materials.map((material) => ({
    ...material,
    proportion: material.mass / totalMass,
  }));

  const needsBoosting = materialData.filter(
    (m) => m.proportion < minPercentage
  );
  const hasEnoughSpace = materialData.filter(
    (m) => m.proportion >= minPercentage
  );

  let finalProportions = materialData.map((m) => m.proportion);

  if (needsBoosting.length > 0 && hasEnoughSpace.length > 0) {
    const extraSpaceNeeded = needsBoosting.reduce(
      (sum, m) => sum + (minPercentage - m.proportion),
      0
    );
    const totalLargerSpace = hasEnoughSpace.reduce(
      (sum, m) => sum + m.proportion,
      0
    );
    const reductionRatio = extraSpaceNeeded / totalLargerSpace;

    finalProportions = materialData.map((material) =>
      material.proportion < minPercentage
        ? minPercentage
        : material.proportion * (1 - reductionRatio)
    );
  }

  // For overfull baskets, scale up the largest material to make total exactly 100%
  if (fullness > 1 && finalProportions.length > 0) {
    // Find the index of the largest material by proportion
    const largestIndex = finalProportions.reduce(
      (maxIndex, current, index, array) =>
        current > (array[maxIndex] ?? 0) ? index : maxIndex,
      0
    );

    // Calculate how much extra we need to remove to get to 100%
    const totalProportion = finalProportions.reduce(
      (sum, prop) => sum + prop,
      0
    );
    const excess = totalProportion - 1; // Amount over 100%

    // Add the excess to the largest material (making it fill the remaining space)
    if (finalProportions[largestIndex] !== undefined) {
      finalProportions[largestIndex] += excess;
    }
  }

  return finalProportions.map((proportion) =>
    Math.max(0, proportion * availableHeight)
  );
};

interface BasketPlotProps {
  materials: BasketMaterial[];
  totalMass: number;
  fullness: number;
  height?: number;
}

export const BasketPlot: React.FC<BasketPlotProps> = ({
  materials,
  totalMass,
  fullness,
  height = TOTAL_HEIGHT,
}) => {
  const theme = useTheme();

  // Calculate the actual height available for materials
  const actualMaterialsHeight = height * Math.min(fullness, 1);

  const segmentHeights = calculateSegmentHeights(
    materials,
    totalMass,
    actualMaterialsHeight,
    fullness
  );

  // Calculate the sum of all segment heights
  const totalSegmentHeights = segmentHeights.reduce(
    (sum, height) => sum + height,
    0
  );

  // Calculate empty space height as height minus sum of segment heights
  const emptySpaceHeight = Math.max(0, height - totalSegmentHeights);

  return (
    <Box
      sx={{
        border: `2px solid ${theme.palette.grey[400]}`,
        borderRadius: 1,
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Empty space - only show if not full and not overfull */}
      {fullness < 1 && (
        <Box
          sx={{
            height: emptySpaceHeight,
            backgroundColor: theme.palette.grey[100],
            borderBottom: `1px dashed ${theme.palette.grey[400]}`,
          }}
        />
      )}

      {/* Materials */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column-reverse",
        }}
      >
        {materials.map((material, index) => (
          <BasketPlotLayer
            key={`${material.name}-${material.mass}`}
            material={material}
            height={segmentHeights[index] ?? 0}
          />
        ))}
      </Box>
    </Box>
  );
};
